import {AggregationType} from "../../../types/AggregationType";
import {SearchPropertyModel} from "../../../types/Search";

const FACET_NAME_SEPARATOR = '-';
const FACET_ITEM_KEY_SEPARATOR = ':';

/**
 * Utility for dealing with aggregations.
 */
export default class AggregationUtil {

  /**
   * Filters the provided properties and returns only those that are supported for aggregations.
   *
   * @param {Array<SearchPropertyModel>} properties the properties to filter
   * @returns {Array<SearchPropertyModel>}
   */
  static filterSupportedProperties(properties: Array<SearchPropertyModel>): Array<SearchPropertyModel> {
    return properties.filter((property: SearchPropertyModel) => {
      if (property.name === "type") {
        return true;
      }
      // Object properties are not supported (currently)
      if (property.isObject) {
        return false;
      }
      // Skip the ID and type properties or any property pointing to others
      if (property.range === "ID") {
        return false;
      }
      // Lang string aggregation is not supported (currently)
      if (property.range === "Literal") {
        return false;
      }
      // Avoid any text properties, ES will error
      return property.aggregationType !== 'text';
    });
  }

  /**
   * Finds out aggregation type by using property range.
   *
   * @param {SearchPropertyModel} property
   * @returns {AggregationType}
   */
  static resolveAggregationTypeFromRange(property: SearchPropertyModel): AggregationType {
    // TODO: this field is conflicting with the AggregationType type
    const aggregationType = property.aggregationType;
    let resolvedType = AggregationType.KEYWORD;
    if (aggregationType === 'date') {
      // TODO: THIS IS KINDA WRONG, for time only fields, this is not correct, or at least in the date picker..
      // TODO: maybe the date picker should remain only for full date related properties..
      resolvedType = AggregationType.DATERANGE;
    } else if (aggregationType === 'numeric') {
      resolvedType = AggregationType.NUMERIC_RANGE;
    }
    return resolvedType;
  }

  /**
   * Creates an aggregation name for the given property and its aggregation type.
   *
   * @param {AggregationType} aggregationType the property's aggregation type
   * @param {SearchPropertyModel} property
   * @returns {string} the constructed type
   */
  static buildAggregationName(aggregationType: AggregationType, property: SearchPropertyModel): string {
    return `${aggregationType}${FACET_NAME_SEPARATOR}${property.range}${FACET_NAME_SEPARATOR}${property.name}`;
  }

  /**
   * Creates a facet item key from the given field data and bucket value.
   *
   * @param {string} field the field to whichj this facet belongs to
   * @param {string} fieldType  the type of the field
   * @param {any} bucketValue the value of the bucket for this facet item
   * @returns {string}
   */
  static buildFacetItemKey(field: string, fieldType: string, bucketValue: any): string {
    return `${fieldType}${FACET_ITEM_KEY_SEPARATOR}${field}${FACET_ITEM_KEY_SEPARATOR}${bucketValue}`;
  }

  /**
   * Splits an aggregation name, that has been built by buildAggregationName into its parts.
   *
   * @param aggregationName the name to split
   * @returns the split parts
   */
  public static splitAggregationName(aggregationName: string): {
    aggregationType: AggregationType
    fieldRange: string
    fieldName: string
  } {
    const keyParts: Array<string> = aggregationName.split(FACET_NAME_SEPARATOR);
    return {
      aggregationType: keyParts[0] as AggregationType,
      fieldRange: keyParts[1],
      fieldName: keyParts[2]
    };
  }
}
